<template>
  <div class="flex relative">
    <!-- Mobile Hamburger Button -->
    <button
      class="hamburger-btn xl:hidden fixed top-4 left-4 z-[1100] rounded p-2 text-xl cursor-pointer shadow-sm"
      aria-label="Open sidebar"
      @click="sidebarOpen = true"
    >
      <i class="pi pi-bars"></i>
    </button>

    <!-- Mobile Backdrop -->
    <div
      v-if="sidebarOpen"
      class="backdrop fixed inset-0 z-[1000] xl:hidden transition-opacity duration-300"
      style="background-color: rgba(0, 0, 0, 0.25)"
      @click="sidebarOpen = false"
    ></div>

    <!-- Sidebar -->
    <aside
      class="w-64 fixed left-0 top-0 bottom-0 z-[1001] transition-transform duration-300 xl:translate-x-0 flex flex-col shadow-sm xl:shadow-none"
      :class="{
        'translate-x-0': sidebarOpen,
        '-translate-x-full': !sidebarOpen,
      }"
    >
      <Menu
        :model="menuItems"
        class="h-full flex flex-col border-none rounded-none"
      >
        <template #start>
          <div class="py-6 px-4 lg:py-8 lg:px-6">
            <div class="flex items-center justify-between">
              <h1 class="text-xl font-semibold text-white">Gleanin Analytics Suite</h1>
              <Button
                variant="text"
                class="xl:hidden p-2"
                aria-label="Close sidebar"
                @click="sidebarOpen = false"
              >
                <i class="pi pi-times text-xl"></i>
              </Button>
            </div>
          </div>
        </template>
        <template #item="{ item, props }">
          <router-link
            class="p-3 lg:p-4"
            v-if="item.route"
            v-slot="{ href, navigate, isActive }"
            :to="item.route"
            custom
          >
            <a
              v-ripple
              :href="href"
              v-bind="props.action"
              @click="navigate"
              :class="{
                'bg-primary text-primary-contrast rounded-sm': isActive,
              }"
              class="flex items-center text-sm lg:text-base"
            >
              <SvgIcon :name="item.icon" :width="24" />
              <span class="ml-2 lg:ml-3">{{ item.label }}</span>
            </a>
          </router-link>
          <a
            class="p-3 lg:p-4 flex items-center text-sm lg:text-base"
            v-else
            v-ripple
            :href="item.url"
            :target="item.target"
            v-bind="props.action"
          >
            <SvgIcon :name="item.icon" :width="24" />
            <span class="ml-2 lg:ml-3">{{ item.label }}</span>
          </a>
        </template>

        <template #end>
          <div class="p-4 lg:p-6 mt-auto">
            <ThemeSwitcher />
          </div>
        </template>
      </Menu>
    </aside>

    <!-- Main Content -->
    <main
      class="main-content flex-1 min-w-0 p-4 sm:p-6 lg:p-8 xl:ml-64"
      @click="sidebarOpen = false"
    >
      <router-view />
    </main>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import Menu from 'primevue/menu'
  import Button from 'primevue/button'
  import SvgIcon from '@/components/SvgIcon.vue'
  import ThemeSwitcher from './ThemeSwitcher.vue'
  import { env } from '../config/env'

  const router = useRouter()
  const route = useRoute()
  const sidebarOpen = ref(false)
  const isMobile = ref(false)

  // Close sidebar when route changes (for mobile)
  watch(route, () => {
    if (isMobile.value) {
      sidebarOpen.value = false
    }
  })

  const checkMobile = () => {
    isMobile.value = window.innerWidth < 1280 // Changed to 1280 for xl breakpoint
    if (!isMobile.value) {
      sidebarOpen.value = false
    }
  }

  onMounted(() => {
    checkMobile()
    window.addEventListener('resize', checkMobile)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', checkMobile)
  })

  const menuItems = computed(() => [
    {
      label: 'Back to Gleanin',
      icon: 'NavGleanin',
      url: env.fleshDomain,
      target: '_self',
    },
    {
      label: 'Dashboard',
      icon: 'NavDashboard',
      route: { name: 'Dashboard' },
    },
    {
      label: 'Roll-up reporting',
      icon: 'NavRollup',
      route: { name: 'Rollup' },
    },
    {
      label: 'Comparative reporting',
      icon: 'NavComparative',
      route: { name: 'Comparative' },
    },
    // {
    //   label: 'Benchmark reporting',
    //   icon: 'NavBenchmark',
    //   route: { name: 'Benchmark' },
    // },
  ])
</script>

<style scoped>
  /* All colors use PrimeVue theme CSS custom properties */
  .hamburger-btn {
    background-color: var(--surface-0);
    border: 1px solid var(--surface-border);
    color: var(--text-color);
  }

  .main-content {
    background-color: var(--ground-background);
    color: var(--p-text-color);
    min-height: 100vh;
  }

  /* Force Menu to take full height and use flexbox */
  :deep(.p-menu) {
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
  }

  :deep(.p-menu .p-menu-list) {
    flex: 1 !important;
    display: flex !important;
    flex-direction: column !important;
  }
</style>
